export interface ThemeColorValue {
  main: string
  light: string
  dark: string
  contrastText: string
}

// Lightness deltas that reproduce REVER's own palette: #1b75eb sits at 51% lightness and its
// shipped light/dark variants (#63a2f4, #0148a5) sit at 67% and 33%.
const LIGHTEN_POINTS = 16
const DARKEN_POINTS = 19

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

export const isHexColor = (value: string): boolean => HEX_COLOR.test(value)

// Derives the three variants the portal needs from the one colour a merchant picks. Returns
// null for anything that is not a hex colour, which is what a half-typed value looks like — the
// caller keeps the previous variants rather than deriving from garbage.
export function deriveThemeColor(main: string): ThemeColorValue | null {
  if (!isHexColor(main)) return null

  const { h, s, l } = toHsl(main)
  return {
    main: toHex({ h, s, l }),
    light: toHex({ h, s, l: clampLightness(l + LIGHTEN_POINTS) }),
    dark: toHex({ h, s, l: clampLightness(l - DARKEN_POINTS) }),
    contrastText: contrastTextFor(main)
  }
}

// WCAG AA for large text. Button labels on the brand colour are large and bold, so this is the
// bar they have to clear.
const READABLE_CONTRAST = 3

// Keeps white on the brand colour for as long as white is readable on it, and falls back to
// black once it is not. Maximising the contrast ratio instead would put black on REVER's own
// blue — white scores 4.38 there against black's 4.79 — which is not what the portal ships or
// what a brand expects on a mid-dark colour. A merchant who wants the other one sets it by hand
// in the advanced variants.
export function contrastTextFor(hex: string): string {
  const luminance = relativeLuminance(hex)
  const againstWhite = 1.05 / (luminance + 0.05)
  return againstWhite >= READABLE_CONTRAST ? '#ffffff' : '#000000'
}

interface Hsl {
  h: number
  s: number
  l: number
}

const clampLightness = (l: number) => Math.min(100, Math.max(0, l))

function toRgb(hex: string): [number, number, number] {
  let value = hex.slice(1)
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('')
  }
  const int = parseInt(value, 16)
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255]
}

function toHsl(hex: string): Hsl {
  const [r, g, b] = toRgb(hex).map((c) => c / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  const l = (max + min) / 2

  if (delta === 0) return { h: 0, s: 0, l: l * 100 }

  const s = delta / (1 - Math.abs(2 * l - 1))
  let h: number
  if (max === r) h = ((g - b) / delta) % 6
  else if (max === g) h = (b - r) / delta + 2
  else h = (r - g) / delta + 4
  h *= 60
  if (h < 0) h += 360

  return { h, s: s * 100, l: l * 100 }
}

function toHex({ h, s, l }: Hsl): string {
  const saturation = s / 100
  const lightness = l / 100
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = lightness - c / 2

  const [r, g, b] = rgbFromSector(h, c, x)
  return (
    '#' +
    [r, g, b]
      .map((channel) =>
        Math.round((channel + m) * 255)
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  )
}

function rgbFromSector(
  h: number,
  c: number,
  x: number
): [number, number, number] {
  if (h < 60) return [c, x, 0]
  if (h < 120) return [x, c, 0]
  if (h < 180) return [0, c, x]
  if (h < 240) return [0, x, c]
  if (h < 300) return [x, 0, c]
  return [c, 0, x]
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((channel) => {
    const s = channel / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}
